
import { defineComponent, computed, ref, Ref, inject } from "vue";
import { fabric } from "fabric";
import axios from "axios";
import { store } from "@/store";
import CloseSVG from "@/components/CloseSVG.vue";
import usePrintPreview from "@/composables/usePrintPreview";
import { checkBleed, setLabelGroupAngle, setClipPath } from "@/canvas-api/PreviewCanvas";
import { ClientDimensions, SheetLabel } from "@/types";

const adjustments: string[] = ["0", '1/64"', '1/32"', '1/16"', '1/8"', '3/16"', '1/4"'];

export default defineComponent({
  name: "AlignmentWizard",
  components: {
    CloseSVG,
  },
  setup() {
    const closeModal = inject("closeModal") as () => void;
    const bounceTransition = inject("bounceTransition") as Ref<string>;
    const fadeTransition = inject("fadeTransition") as Ref<string>;

    const clientDimensions = computed<ClientDimensions>(() => store.getters.clientDimensions);
    const currentLabel = computed<SheetLabel>(() => store.getters.currentLabel);
    const isRotated = computed<boolean>(() =>
      store.getters.canvasOrientation === "rotated" ? true : false
    );

    const loading = ref(false);

    const upAdjust = ref(0);
    const downAdjust = ref(0);
    const leftAdjust = ref(0);
    const rightAdjust = ref(0);

    const upDisable = computed<boolean>(() => (downAdjust.value !== 0 ? true : false));
    const downDisable = computed<boolean>(() => (upAdjust.value !== 0 ? true : false));
    const leftDisable = computed<boolean>(() => (rightAdjust.value !== 0 ? true : false));
    const rightDisable = computed<boolean>(() => (leftAdjust.value !== 0 ? true : false));

    const handleChange = (ev: { target: HTMLOptionElement }) => {
      switch (ev.target.getAttribute("name")) {
        case "up-adjust": {
          const value = ev.target.value.split('"');
          upAdjust.value = fractionToDecimal(value[0]);
          break;
        }
        case "down-adjust": {
          const value = ev.target.value.split('"');
          downAdjust.value = fractionToDecimal(value[0]);
          break;
        }
        case "left-adjust": {
          const value = ev.target.value.split('"');
          leftAdjust.value = fractionToDecimal(value[0]);
          break;
        }
        case "right-adjust": {
          const value = ev.target.value.split('"');
          rightAdjust.value = fractionToDecimal(value[0]);
          break;
        }
      }
    };

    const fractionToDecimal = (inches: string): number => {
      const split = inches.split("/");
      const numerator = Number(split[0]);
      const denominator = Number(split[1]);

      if (denominator) {
        return numerator / denominator;
      } else {
        return 0;
      }
    };

    const cloneGroupForServer = (
      labelGroup: fabric.Group,
      label: fabric.Object
    ): Promise<fabric.Group> => {
      const { x: labelLeft, y: labelTop } = label.getCenterPoint();

      return new Promise((resolve) => {
        labelGroup.clone(function (clone: fabric.Group) {
          clone.set({
            scaleX: 1,
            scaleY: 1,
            originX: "center",
            originY: "center",
          });

          if (isRotated.value) {
            clone.set({
              left: -labelTop + label.getScaledWidth() / 2,
              top: labelLeft + label.getScaledHeight() / 2,
            });
          } else {
            clone.set({
              left: -labelLeft + label.getScaledWidth() / 2,
              top: -labelTop + label.getScaledHeight() / 2,
            });
          }

          resolve(clone);
        });
      });
    };

    const printSheet = (groupToPrint: fabric.Group, loading: Ref<boolean>) => {
      fabric.Object.NUM_FRACTION_DIGITS = 8;

      const cl = currentLabel.value;

      let labelWidth = cl.width;
      let labelHeight = cl.height;
      let leftMargin = cl.left_margin;
      let topMargin = cl.top_margin;
      let horizontalSpacing = cl.horizontal_spacing;
      let verticalSpacing = cl.vertical_spacing;

      if (cl.bleed) {
        if (cl.bleed_size) {
          labelWidth += cl.bleed_size * 2;
          labelHeight += cl.bleed_size * 2;

          leftMargin -= cl.bleed_size;
          topMargin -= cl.bleed_size;

          horizontalSpacing -= cl.bleed_size * 2;
          verticalSpacing -= cl.bleed_size * 2;
        } else {
          labelWidth += 0.125 * 2;
          labelHeight += 0.125 * 2;

          leftMargin -= 0.125;
          topMargin -= 0.125;

          horizontalSpacing -= 0.125 * 2;
          verticalSpacing -= 0.125 * 2;
        }
      }

      // User can only select one or the other for left/right and up/down adjustments
      // One will have a value and the other will be 0, or all will be 0
      leftMargin -= leftAdjust.value;
      leftMargin += rightAdjust.value;
      topMargin -= upAdjust.value;
      topMargin += downAdjust.value;

      let data = JSON.stringify({
        svg: groupToPrint.toSVG(),
        strokeWidth: 1,
        labelWidth,
        labelHeight,
        leftMargin,
        topMargin,
        horizontalSpacing,
        verticalSpacing,
        labelsPerRow: cl.labels_per_row,
        labelsPerSheet: cl.labels_per_sheet,
        sheetSizeWidth: cl.sheet_size_width,
        sheetSizeHeight: cl.sheet_size_height,
      });

      axios
        .post(process.env.VUE_APP_NODE_URL, data, {
          headers: { "Content-Type": "application/json" },
          responseType: "arraybuffer",
        })
        .then((res) => {
          var blob = new Blob([res.data], {
            type: "application/pdf",
          });
          var fileName = "SheetLabels Label Design.pdf";
          var elem = window.document.createElement("a");
          elem.href = window.URL.createObjectURL(blob);
          elem.download = fileName;
          document.body.appendChild(elem);
          elem.click();
          document.body.removeChild(elem);

          loading.value = false;
        })
        .catch((err) => {
          loading.value = false;
          console.log(err);
        });

      fabric.Object.NUM_FRACTION_DIGITS = 4;
    };

    const setupLabel = async () => {
      loading.value = true;

      const labelGroup = await usePrintPreview();

      if (labelGroup) {
        setLabelGroupAngle(labelGroup, isRotated.value);
        const label = checkBleed(labelGroup, currentLabel.value.bleed);

        if (label) {
          label.set({
            strokeWidth: 0,
          });

          await setClipPath(
            labelGroup,
            label,
            currentLabel.value.shape_id,
            currentLabel.value.mask_id,
            currentLabel.value.mask_url
          );

          const clone = await cloneGroupForServer(labelGroup, label);
          printSheet(clone, loading);
        }
      }
    };

    return {
      closeModal,
      bounceTransition,
      fadeTransition,
      clientDimensions,
      upDisable,
      downDisable,
      leftDisable,
      rightDisable,
      adjustments,
      handleChange,
      loading,
      setupLabel,
    };
  },
});
